import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Avatar,
    Chip
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useTheme } from '@mui/material/styles';

const PROVIDER_LOGO_PATH = '/images/providers/';

const ConfirmPlanChangeModal = ({ open, onClose, onConfirm, selectedPlan }) => {
    const theme = useTheme();

    const getColorCode = (color) => {
        return color === 'green' ? '#4CAF50' : '#2196F3';
    };

    const getBgColorCode = (color) => {
        return color === 'green' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(33, 150, 243, 0.1)';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: '25px',
                    width: '500px',
                    maxWidth: '90vw',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                    sx={{
                        backgroundColor: theme.palette.warning.light,
                        width: 48,
                        height: 48
                    }}
                >
                    <WarningIcon sx={{ color: 'white' }} />
                </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight="bold">
                        Confirm Plan Change
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to change your electricity plan?
                </Typography>
                {selectedPlan && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            padding: '16px',
                            marginBottom: '12px',
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            border: `1px solid ${theme.palette.grey[400]}`,
                        }}
                    >
                        {/* Provider Logo */}
                        <Avatar
                            src={PROVIDER_LOGO_PATH + selectedPlan.provider_name + '.png'}
                            alt={selectedPlan.provider_name}
                            sx={{
                                width: 56,
                                height: 56,
                                bgcolor: getBgColorCode(selectedPlan.color),
                                border: `2px solid ${getColorCode(selectedPlan.color)}`,
                            }}
                        />

                        {/* Plan Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                                {selectedPlan.display_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip
                                    label={(selectedPlan.provider_name || '').replace(/_/g, ' ')}
                                    size="small"
                                    sx={{
                                        backgroundColor: getBgColorCode(selectedPlan.color),
                                        color: getColorCode(selectedPlan.color),
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        height: '24px',
                                    }}
                                />
                            </Box>
                            {selectedPlan.description && (
                                <Typography variant="caption" color="text.secondary">
                                    {selectedPlan.description}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
                <Typography variant="body2" color="text.secondary">
                    Changing your electricity plan will cause all billing calculations to be redone. This action cannot be undone.
                </Typography>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        color: 'white',
                        boxShadow: 'none',
                        '&:hover': {
                            background: `linear-gradient(to right, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                            boxShadow: 'none',
                        },
                    }}
                >
                    Yes, Change Plan
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmPlanChangeModal;

